import type { Nullish } from '@aigc/types';
import { match, P } from 'ts-pattern';
import type { ComplexPromptFormValues } from '../video/schema';
import type { MultiPromptDto } from './multi-prompt-model';

export const scriptTemplate = `## 镜头脚本

[镜号]：1
[时长]：5秒
[镜头目的]：
[镜头]：
[运镜]：
[画面描述]：
[台词/字幕/情绪]：

## 要点
`;

export function buildPrompt(segment: Partial<MultiPromptDto>, index: number) {
  let content = `[镜号]：${index + 1}`;

  if (segment.duration) {
    content += `\n[时长]：${segment.duration}秒`;
  }

  if (segment.shot_purpose) {
    content += `\n[镜头目的]：${segment.shot_purpose}`;
  }

  if (segment.shot_type) {
    content += `\n[镜头]：${segment.shot_type}`;
  }

  if (segment.camera_movement) {
    content += `\n[运镜]：${segment.camera_movement}`;
  }

  content += `\n[画面描述]：${segment.visual_description}`;

  if (segment.dialogue_subtitle_emotion) {
    content += `\n[台词/字幕/情绪]：${segment.dialogue_subtitle_emotion}`;
  }

  if (segment.visual_keywords?.length) {
    content += `\n[画面关键词]：${segment.visual_keywords.join('、')}`;
  }

  return content;
}

export function buildMultiPrompt(
  segments?: Partial<MultiPromptDto>[],
  extra_prompt?: Nullish<string>,
) {
  if (!segments?.length) {
    return '';
  }

  let prompt = '## 镜头脚本\n\n' + segments.map(buildPrompt).join('\n\n');

  if (extra_prompt) {
    prompt += '\n\n## 要点\n\n' + extra_prompt;
  }

  return prompt;
}

export function getDescription(segment: MultiPromptDto) {
  return match(segment)
    .with(
      {
        visual_description: null,
        prompt: P.select(P.string),
      },
      (value) => value,
    )
    .otherwise(() => segment.visual_description);
}

export function getDefaultMultiPrompt(): ComplexPromptFormValues {
  return {
    duration: 5,
    camera_movement: '',
    dialogue_subtitle_emotion: '',
    shot_purpose: '',
    shot_type: '',
    visual_description: '',
    visual_keywords: null,
  };
}

/**
 * 将媒体宽高限制在 720p 以下
 * @param width 宽
 * @param height 高
 * @returns
 */
export function resize(
  width: number,
  height: number,
): [width: number, height: number] {
  const shortSide = Math.min(width, height);

  if (shortSide > 0 && shortSide < 720) {
    return [width, height];
  }

  const ratio = 720 / shortSide;

  const shortSideIndex = [width, height].indexOf(shortSide);
  const longSideIndex = (shortSideIndex + 1) % 2;

  const longSide = [width, height][longSideIndex] ?? 0;

  const size: [width: number, height: number] = [0, 0];

  size[shortSideIndex] = 720;
  size[longSideIndex] = longSide * ratio;

  return size;
}
